import clsx from 'clsx'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { CloseIcon } from '@/shared/icons'
import { lockBodyScroll } from '@/shared/lib/scrollLock'

import s from './Lightbox.module.css'

interface LightboxProps {
  src: string
  /** Подпись в шапке: время кадра и камера. */
  caption?: string
  onClose: () => void
}

/**
 * Снимок во весь экран поверх страницы. Тап по снимку переключает «вписать в экран» ↔
 * увеличенный кадр с прокруткой — на телефоне вписанный кадр 2:1 слишком мелкий,
 * чтобы что-то разглядеть.
 * Рендерится порталом в body, чтобы не зависеть от контекста наложения панели деталей.
 */
export function Lightbox({ src, caption, onClose }: LightboxProps) {
  const [zoomed, setZoomed] = useState(false)
  const [zoomWidth, setZoomWidth] = useState(0)
  const closeRef = useRef<HTMLButtonElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const unlock = lockBodyScroll()
    closeRef.current?.focus()
    // capture + stopPropagation: Escape закрывает только лайтбокс, а не панель деталей под ним.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      unlock()
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [onClose])

  // После увеличения ставим прокрутку в центр кадра.
  useLayoutEffect(() => {
    const el = viewportRef.current
    if (!zoomed || !el) return
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2
  }, [zoomed, zoomWidth])

  const toggleZoom = () => {
    if (zoomed) {
      setZoomed(false)
      return
    }
    // Увеличиваем до исходного размера, но не меньше двух экранов по ширине:
    // на большом мониторе исходный кадр может оказаться мельче вписанного.
    const natural = imageRef.current?.naturalWidth ?? 0
    const viewport = viewportRef.current?.clientWidth ?? window.innerWidth
    setZoomWidth(Math.max(natural, viewport * 2))
    setZoomed(true)
  }

  return createPortal(
    <div
      className={s.root}
      role="dialog"
      aria-modal="true"
      aria-label="Снимок события"
      // Клики не должны всплывать в панель деталей (портал сохраняет всплытие React-событий).
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    >
      <div className={s.bar} onClick={(e) => e.stopPropagation()}>
        {caption && <span className={s.caption}>{caption}</span>}
        <button
          ref={closeRef}
          type="button"
          className={s.close}
          title="Закрыть"
          aria-label="Закрыть"
          onClick={onClose}
        >
          <CloseIcon size={18} />
        </button>
      </div>
      <div ref={viewportRef} className={clsx(s.viewport, zoomed && s.zoomed)}>
        <div className={s.canvas}>
          <img
            ref={imageRef}
            className={clsx(s.picture, zoomed && s.pictureZoomed)}
            style={zoomed ? { width: zoomWidth } : undefined}
            src={src}
            alt=""
            draggable={false}
            role="button"
            tabIndex={0}
            aria-label={zoomed ? 'Уменьшить снимок' : 'Увеличить снимок'}
            onClick={(e) => {
              e.stopPropagation()
              toggleZoom()
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              toggleZoom()
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
